import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { atom, useAtom, useSetAtom } from "jotai";

import AddViewDrawer from "../../components/PageView/AddViewDrawer/AddViewDrawer";
import useUserActivity from "../../api/api-hooks/userActivity/useUserActivity";
import PageViewRenderer from "../../components/PageView/PageViewRenderer";
import { Page, PageView as PView } from "../../types/GlobalInterfaces";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import PageViewHeader from "../../components/PageView/PageViewHeader";
import PageViewNavbar from "../../components/PageView/PageViewNavbar";
import useView from "../../api/api-hooks/views/useView";
import usePage from "../../api/api-hooks/pages/usePage";
import { currentViewAtom } from "../../atoms/ViewAtoms";
import StrUtils from "../../utils/StrUtils";
import { ErrorBoundary } from "react-error-boundary";
import { CrashViewComponent } from "../other/CrashView";

export const HeaderWrapper = ({
  loading,
  children,
}: {
  loading: boolean;
  children: ReactNode;
}) => {
  return (
    <div
      className="tw:sm:px-6"
      style={{ borderBottom: loading ? "none" : "1px solid #EEE" }}
    >
      {children}
    </div>
  );
};

export const insightsOpenAtom = atom<Record<string, boolean>>({});

interface PageViewProps {
  pageID?: string;
  viewID?: string;
  tenantID?: string;
  workspaceID?: string;
}

const PageView = (props: PageViewProps) => {
  const {
    pageID: propPageID,
    viewID: propViewID,
    tenantID: propTenantID,
    workspaceID: propWorkspaceID,
  } = props;
  const {
    tenantID: paramTenantID,
    workspaceID: paramWorkspaceID,
    pageID: paramPageID,
    viewID: paramViewID,
  }: any = useParams();

  // Use params if they exist, otherwise use the props
  const pageID = paramPageID || propPageID;
  const viewID = paramViewID || propViewID;
  const tenantID = paramTenantID || propTenantID;
  const workspaceID = paramWorkspaceID || propWorkspaceID;

  const [addViewDrawer, setAddViewDrawer] = useState(false);
  const setCurrentView = useSetAtom(currentViewAtom);
  const { page, pageLoading } = usePage(pageID);
  const { workspaceLoading } = useWorkspace();
  const { setUserActivity } = useUserActivity();
  const { view, viewLoading } = useView({ pageID, viewID });
  const [showInsights, setShownInsights] = useAtom(insightsOpenAtom);
  const navigate = useNavigate();

  const pageRef = useRef<Page>();
  const timeoutIDRef = useRef<NodeJS.Timeout>();
  pageRef.current = page;

  useEffect(() => {
    if (pageLoading || !page?.Views?.length) {
      return;
    }

    setCurrentView(page?.Views?.find((v: PView) => v.id === viewID) || null);
  }, [pageLoading, page, viewID]);

  useEffect(() => {
    if (viewLoading || !view) return;

    clearTimeout(timeoutIDRef.current);

    const timeoutID = setTimeout(() => {
      const viewedPageViewActivity = {
        id: StrUtils.uuidv4(),
        Type: "viewed-page-view",
        Page: {
          id: pageID,
          Name: page?.Name,
          Logo: page?.Icon,
        },
        View: {
          id: view?.id,
          Name: view?.Name,
          Type: view?.Type,
          MobileType: view?.MobileType,
        },
      };

      // @ts-ignore
      setUserActivity({
        Activity: viewedPageViewActivity,
      });
    }, 5000);

    timeoutIDRef.current = timeoutID;
  }, [pageID, view, viewLoading]);

  useEffect(() => {
    if (pageLoading || workspaceLoading) return;
    if (pageID !== page?.id) return;
    const views = pageRef.current?.Views?.filter((view) => !view.Hide) ?? [];

    const viewIds = views?.map((view) => view.id);

    if (viewIds.length === 0) return;

    if (!viewIds.includes(viewID)) {
      setCurrentView(pageRef.current?.Views?.[0] || null);
      navigate(
        `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${pageID}/views/${
          viewIds[0] ?? "None"
        }`,
        { replace: true }
      );
    }
  }, [
    page?.id,
    history,
    pageID,
    workspaceID,
    viewID,
    pageLoading,
    workspaceLoading,
    tenantID,
  ]);

  return (
    <>
      <HeaderWrapper loading={pageLoading}>
        <PageViewHeader
          view={view}
          pageID={pageID}
          showInsights={view ? showInsights[view.id] ?? true : true}
          toggleInsights={
            view
              ? () =>
                  setShownInsights((prev) => ({
                    ...prev,
                    [view.id]: prev.hasOwnProperty(view.id)
                      ? !prev[view.id]
                      : false,
                  }))
              : undefined
          }
        />
        <PageViewNavbar
          view={view}
          workspaceID={workspaceID}
          pageID={pageID}
          tenantID={tenantID}
          setAddViewDrawer={setAddViewDrawer}
        />
      </HeaderWrapper>
      <ErrorBoundary
        FallbackComponent={CrashViewComponent}
        resetKeys={[viewID]}
      >
        <PageViewRenderer
          key={viewID}
          page={page}
          pageLoading={pageLoading}
          view={view}
          viewLoading={viewLoading}
          viewID={viewID}
          setAddViewDrawer={setAddViewDrawer}
          showInsights={view ? showInsights?.[view?.id] ?? true : true}
          reusableViews={view?.ReusableViewIDs}
        />
      </ErrorBoundary>
      <AddViewDrawer
        open={addViewDrawer}
        handleClose={() => setAddViewDrawer(false)}
        globalAppID={page?.GlobalAppID}
      />
    </>
  );
};

export default PageView;
